import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { LoginComponent } from './components/login/login.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService } from './services/authentication.service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Configuration } from '@mol-limo-reg/admin-api';
import { ConfigService, initializeConfig } from './core/services/config.service';
import { AdmintoolLayoutComponent } from './components/admintool-layout/admintool-layout.component';
import { BlacklistComponent } from './components/blacklist/blacklist.component';
import { MatTableModule } from '@angular/material/table';
import { DeleteBlacklistItemDialogComponent } from './components/dialogs/delete-blacklist-item-dialog/delete-blacklist-item-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { AddBlacklistItemDialogComponent } from './components/dialogs/add-blacklist-item-dialog/add-blacklist-item-dialog.component';
import { SuccessSnackbarComponent } from './components/snackbars/success-snackbar/success-snackbar.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ErrorSnackbarComponent } from './components/snackbars/error-snackbar/error-snackbar.component';
import { AdminsComponent } from './components/admins/admins.component';
import { EditAdminDialogComponent } from './components/dialogs/edit-admin-dialog/edit-admin-dialog.component';
import { AddAdminDialogComponent } from './components/dialogs/add-admin-dialog/add-admin-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivateAdminDialogComponent } from './components/dialogs/activate-admin-dialog/activate-admin-dialog.component';
import { DeactivateAdminDialogComponent } from './components/dialogs/deactivate-admin-dialog/deactivate-admin-dialog.component';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { PaginatorHungarianIntl } from './core/i18n/paginator-hungarian-intl';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { AuthInterceptor } from './core/auth/auth-interceptor';
import { AuthErrorInterceptor } from './core/auth/auth-error-interceptor';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatRadioModule } from '@angular/material/radio';
import { AdminPermissionsCheckboxGroupComponent } from './components/admin-permissions-checkbox-group/admin-permissions-checkbox-group.component';
import { CopyManagementComponent } from './components/copy-management/copy-management.component';
import { EditCopyDialogComponent } from './components/dialogs/edit-copy-dialog/edit-copy-dialog.component';
import { StationConfiguratorComponent } from './components/station-configurator/station-configurator.component';
import { EditStationDialogComponent } from './components/dialogs/edit-station-dialog/edit-station-dialog.component';
import { ManageCarsComponent } from './components/manage-cars/manage-cars.component';
import { EditCarDialogComponent } from './components/dialogs/edit-car-dialog/edit-car-dialog.component';
import { CarImageUploadDialogComponent } from './components/dialogs/car-image-upload-dialog/car-image-upload-dialog.component';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { provideSvgIconsConfig } from '@ngneat/svg-icon';
import { icons } from 'src/assets/icons';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { SharedComponentsModule } from './shared/shared-components.module';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    AdmintoolLayoutComponent,
    BlacklistComponent,
    DeleteBlacklistItemDialogComponent,
    AddBlacklistItemDialogComponent,
    SuccessSnackbarComponent,
    ErrorSnackbarComponent,
    AdminsComponent,
    EditAdminDialogComponent,
    AddAdminDialogComponent,
    ActivateAdminDialogComponent,
    DeactivateAdminDialogComponent,
    AdminPermissionsCheckboxGroupComponent,
    CopyManagementComponent,
    EditCopyDialogComponent,
    EditStationDialogComponent,
    StationConfiguratorComponent,
    ManageCarsComponent,
    EditCarDialogComponent,
    CarImageUploadDialogComponent,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatDialogModule,
    MatSnackBarModule,
    MatMenuModule,
    MatListModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatSelectModule,
    MatSidenavModule,
    MatRadioModule,
    SvgIconComponent,
    StoreModule.forRoot({}),
    EffectsModule.forRoot(),
    SharedComponentsModule,
  ],
  providers: [
    AuthenticationService,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initializeConfig,
      deps: [ConfigService],
    },
    {
      provide: Configuration,
      useFactory: (configService: ConfigService) =>
        new Configuration({
          basePath: configService.get('apiBaseUrl'),
        }),
      deps: [ConfigService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorHungarianIntl,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'hu-HU',
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true },
    },
    provideSvgIconsConfig({
      sizes: {
        xs: '12px',
        sm: '16px',
        md: '24px',
        lg: '32px',
        xl: '40px',
        xxl: '48px',
        '3xl': '64px',
        '4xl': '88px',
      },
      defaultSize: 'md',
      icons: icons,
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
